import Button from "@mui/material/Button";
import { MonitorSmartphone } from "lucide-react";
import { useMemo } from "react";
import { useChatStore, type PageContext } from "../stores/chat";
interface ChatContextConsentProps {
  pageContext: PageContext | null;
  onSend: () => Promise<void>;
}
export function ChatContextConsent({
  pageContext,
  onSend: handleSend,
}: ChatContextConsentProps) {
  const pendingScreenDetection = useChatStore((s) => s.pendingScreenDetection);
  const includeScreenContext = useChatStore((s) => s.includeScreenContext);
  const includeFormContext = useChatStore((s) => s.includeFormContext);
  const pendingFormDetection = useChatStore((s) => s.pendingFormDetection);
  const screenContextPreference = useChatStore(
    (s) => s.screenContextPreference,
  );
  const setScreenContextPreference = useChatStore(
    (s) => s.setScreenContextPreference,
  );
  const formContextPreference = useChatStore((s) => s.formContextPreference);
  const setFormContextPreference = useChatStore(
    (s) => s.setFormContextPreference,
  );
  const setPendingScreenDetection = useChatStore(
    (s) => s.setPendingScreenDetection,
  );
  const setPendingFormDetection = useChatStore(
    (s) => s.setPendingFormDetection,
  );
  const setIncludeScreenContext = useChatStore(
    (s) => s.setIncludeScreenContext,
  );
  const setIncludeFormContext = useChatStore((s) => s.setIncludeFormContext);
  const screenContextPreview = useMemo(() => {
    const sc = pageContext?.screenContext;
    if (!sc) return null;
    if (sc.pageTitle) return sc.pageTitle;
    if (sc.activeView) return sc.activeView.replace(/-/g, " ");
    return sc.elements.length
      ? sc.elements[0].replace(/-/g, " ")
      : pageContext?.page.replace(/-/g, " ");
  }, [pageContext]);
  const askForScreen =
    pendingScreenDetection &&
    includeScreenContext === null &&
    screenContextPreference === "ask";
  const askForForm =
    pendingFormDetection &&
    includeFormContext === null &&
    formContextPreference === "ask";
  const contextNames: string[] = [];
  if (askForScreen)
    contextNames.push(
      screenContextPreview
        ? "on-screen context from " + screenContextPreview
        : "on-screen context",
    );
  if (askForForm) contextNames.push("facility form context");
  const question = "Include " + contextNames.join(" and ") + "?";

  return (
    <>
      {(askForScreen || askForForm) && (
        <div
          role="status"
          aria-atomic="true"
          /* In normal flow, not floating: as an absolutely positioned bar it
               covered the last row of the generated table above the input. */
          className="mb-2"
        >
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary-5 px-3 py-2">
            <MonitorSmartphone className="h-4 w-4 shrink-0 text-primary" />
            <span className="text-xs text-foreground truncate">{question}</span>
            <div className="flex items-center gap-1.5 ml-auto shrink-0">
              <Button
                variant="contained"
                size="small"
                type="button"
                onClick={() => {
                  if (
                    pendingScreenDetection &&
                    screenContextPreference === "ask"
                  ) {
                    setIncludeScreenContext(true);
                  }
                  if (pendingFormDetection && formContextPreference === "ask") {
                    setIncludeFormContext(true);
                  }
                  setPendingScreenDetection(false);
                  setPendingFormDetection(false);
                  void handleSend();
                }}
                aria-label={
                  screenContextPreview
                    ? `Answer using on-screen context from ${screenContextPreview}`
                    : "Answer using on-screen context"
                }
                sx={{ minWidth: 0 }}
              >
                Yes
              </Button>
              <Button
                variant="text"
                size="small"
                type="button"
                onClick={() => {
                  if (pendingScreenDetection) setIncludeScreenContext(false);
                  if (pendingFormDetection) setIncludeFormContext(false);
                  setPendingScreenDetection(false);
                  setPendingFormDetection(false);
                  void handleSend();
                }}
                sx={{ minWidth: 0, color: "var(--muted-foreground)" }}
              >
                No
              </Button>
              <Button
                variant="text"
                size="small"
                type="button"
                onClick={() => {
                  if (pendingScreenDetection) {
                    setScreenContextPreference("always");
                  }
                  if (pendingFormDetection) {
                    setFormContextPreference("always");
                  }
                  setPendingScreenDetection(false);
                  setPendingFormDetection(false);
                  void handleSend();
                }}
                aria-label="Include context with every message; change this in Context sharing"
                sx={{ minWidth: 0, color: "var(--muted-foreground)" }}
              >
                Always
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
